import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../app/hooks';
import SuperPaymentForm from '../components/SuperPaymentForm';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { Redirect, Route, Switch } from 'react-router-dom';
import PurchaseGiftCardOptions from './PurchaseGiftCardOptions';
import Text from './Text';
import NonMemberGiftCardFields from './NonMemberGiftCardFields';
import { OrderingConstants, OrderingSelectors } from 'polygon-ordering';
import FormTextField from '../components/FormTextField';
import GiftCardDeliveryDetails from '../components/GiftCardDeliveryDetails';
import GiftCardPurchaseRedcatPay from './GiftCardPurchaseRedcatPay';
import { Show } from './atoms/Show';
import moment from 'moment-timezone';
import { noXssCharsTest } from '../utils/yupSchemaTests';
import StandardButton from './StandardButton';
import LoyaltyPaymentScreen from '../screens/LoyaltyPaymentScreen';
import history from '../history';
import {
  PURCHASE_GIFT_CARD_ROUTE,
  OTHERS_FIELDS_ROUTE,
  OTHERS_PAYMENT_ROUTE,
} from '../constants/loyaltyRoutes';

const { getMember, getGatewayType } = OrderingSelectors;
const { PAYMENT_GATEWAY_TYPES } = OrderingConstants;

const PurchaseGiftCardForOthersFields = () => {
  const { t } = useTranslation();
  const member = useAppSelector(getMember);
  const gatewayType = useAppSelector(getGatewayType);
  const { handleChange, isValid } = useFormikContext<any>();

  const fields = [
    { name: 'RecipientName', type: 'text', label: t('placeholder.firstName'), required: true },
    { name: 'RecipientSurname', type: 'text', label: t('placeholder.lastName'), required: true },
    { name: 'RecipientEmail', type: 'email', label: t('placeholder.email'), required: true },
    { name: 'Memo', type: 'text', label: t('placeholder.message'), required: false },
  ];

  return (
    <div>
      {!member && <NonMemberGiftCardFields />}
      <Text value={t('recipientDetails')} style={{ fontSize: 15 }} themeKey="recipientDetails" />
      {fields.map(field => (
        <FormTextField
          key={field.name}
          type={field.type}
          name={field.name}
          label={field.label}
          onChange={handleChange}
          required={field.required}
          multiline={field.name.toLowerCase() === 'memo'}
        />
      ))}
      <GiftCardDeliveryDetails />
      <PurchaseGiftCardOptions />
      <Show when={gatewayType === PAYMENT_GATEWAY_TYPES.REDCAT_PAY}>
        <div style={styles.buttonContainer}>
          <StandardButton
            label={t('button.proceedToPayment')}
            disabled={!isValid}
            onClick={() => history.push(OTHERS_PAYMENT_ROUTE)}
            themeKey="loyaltyButton"
            containerStyle={styles.button}
          />
        </div>
      </Show>
      <Show when={gatewayType !== PAYMENT_GATEWAY_TYPES.REDCAT_PAY}>
        <SuperPaymentForm
          route={member ? 'memberPurchaseGiftCardForOthers' : 'nonMemberPurchaseGiftCardForOthers'}
          notRequired={['Memo', 'SendTime']}
        />
      </Show>
    </div>
  );
};

const PurchaseGiftCardForOthers = () => {
  const { t } = useTranslation();
  const member = useAppSelector(getMember);
  const gatewayType = useAppSelector(getGatewayType);
  const maxGiftCardValue = useAppSelector(state => state.config.maxGiftCardValue);

  const isSameOrAfter = (startTime: moment.Moment, endTime: moment.Moment) =>
    moment(startTime, 'YYYY-MM-DDTHH:mmZ').isSameOrAfter(moment(endTime, 'YYYY-MM-DDTHH:mmZ'));

  const baseSchema = {
    RecipientName: noXssCharsTest.required(t('form.required')),
    RecipientSurname: noXssCharsTest.required(t('form.required')),
    RecipientEmail: Yup.string().email(t('form.emailValidation')).required(t('form.required')),
    Memo: noXssCharsTest,
    Amount: Yup.number()
      .required(t('form.required'))
      .min(1)
      .test('is-less-than-max-amount', `Maximum $${maxGiftCardValue}`, value => {
        if (value) return Number(value) <= maxGiftCardValue;
        return false;
      }),
    delivery: Yup.boolean(),
    SendTime: Yup.date().when('delivery', {
      is: true,
      then: Yup.date().test({
        name: 'SendTime',
        message: t('form.scheduleGiftcard'),
        test: function (value: any) {
          const { timezone } = this.parent;
          const dateWithTimezone = moment.tz(moment(value).format('YYYY-MM-DD HH:mm:ss'), timezone);
          const currentTime = moment().tz(timezone);
          return isSameOrAfter(dateWithTimezone, currentTime);
        },
      }),
    }),
  };

  const nonMemberSchema = {
    ...baseSchema,
    FirstName: noXssCharsTest.required(t('form.required')),
    LastName: noXssCharsTest.required(t('form.required')),
    Email: Yup.string().email(t('form.emailValidation')).required(t('form.required')),
    ConfirmEmail: Yup.string()
      .email()
      .required(t('form.required'))
      .oneOf([Yup.ref('Email')], t('form.valuesMismatch')),
  };

  const memberInitialValues = {
    Amount: '',
    RecipientName: '',
    RecipientSurname: '',
    RecipientEmail: '',
    Memo: '',
    SendTime: '',
    timezone: 'Australia/Melbourne',
    delivery: false,
  };

  const nonMemberInitialValues = {
    Amount: '',
    FirstName: '',
    LastName: '',
    Email: '',
    ConfirmEmail: '',
    RecipientName: '',
    RecipientSurname: '',
    RecipientEmail: '',
    Memo: '',
    SendTime: '',
    timezone: 'Australia/Melbourne',
    delivery: false,
  };

  return (
    <Formik
      validationSchema={
        member ? Yup.object().shape(baseSchema) : Yup.object().shape(nonMemberSchema)
      }
      initialValues={member ? memberInitialValues : nonMemberInitialValues}
      onSubmit={() => {}}
      validateOnMount={true}
    >
      {({ handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          {gatewayType === PAYMENT_GATEWAY_TYPES.REDCAT_PAY ? (
            <Switch>
              <Redirect
                exact
                from={`${PURCHASE_GIFT_CARD_ROUTE}/others`}
                to={OTHERS_FIELDS_ROUTE}
              />
              <Route path={OTHERS_FIELDS_ROUTE}>
                <PurchaseGiftCardForOthersFields />
              </Route>
              <Route path={OTHERS_PAYMENT_ROUTE}>
                <LoyaltyPaymentScreen>
                  <GiftCardPurchaseRedcatPay />
                </LoyaltyPaymentScreen>
              </Route>
            </Switch>
          ) : (
            <PurchaseGiftCardForOthersFields />
          )}
        </Form>
      )}
    </Formik>
  );
};

const styles: Styles = {
  buttonContainer: {
    marginTop: 20,
    display: 'flex',
    justifyContent: 'center',
  },
  button: {
    borderRadius: 5,
    height: 40,
    width: '100%',
  },
};

export default PurchaseGiftCardForOthers;
